import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    RefreshCw, Upload, MessageSquare, Check, X, Loader2, Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { studioHeaders } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const absUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http://") || u.startsWith("https://")) return u;
    return `${BACKEND}${u}`;
};

/**
 * Combined image + text preview-editor for the 30-day calendar.
 *
 * Mirrors the customer's Kit page layout (image + caption side-by-side per
 * day, Day 1→30 order) so the admin reviews exactly what the customer will
 * see. Each day's image gets three surgical edit controls:
 *   - Re-roll: one-click new render with the same prompt
 *   - Re-roll with hint: inline text input → prompt appended with HIGH
 *     PRIORITY directive (e.g. "darker mood")
 *   - Upload: file picker → admin-supplied PNG/JPG replaces the AI render
 *
 * All three stage their result as `preview_url`; admin then Accepts or
 * Discards. Until Accept, the customer sees no change.
 */
export default function CalendarPreviewEditor({
    data, onChange, orderId, images, onImagesRefresh,
}) {
    const days = data || [];

    // Lookup table: (day, slide) → image entry. Lets each day card grab its
    // own image without scanning the whole array per render.
    const imageByKey = useMemo(() => {
        const map = new Map();
        (images || []).forEach((img) => {
            map.set(`${img.day}-${img.slide || 1}`, img);
        });
        return map;
    }, [images]);

    return (
        <div className="space-y-5" data-testid="calendar-preview-editor">
            <div className="rounded-2xl border border-cafe-olive/30 bg-cafe-olive/5 px-4 py-3 text-xs text-cafe-mocha leading-relaxed">
                <p className="font-semibold text-cafe-olive uppercase tracking-[0.2em] text-[10px] mb-1">
                    Customer-view preview
                </p>
                <p>
                    This is exactly how Day 1→30 will render on the customer's Kit page.
                    Edit captions inline; for any image that's off-style, use{" "}
                    <span className="font-semibold">Re-roll</span> (same prompt),{" "}
                    <span className="font-semibold">+Hint</span> (steer the AI), or{" "}
                    <span className="font-semibold">Upload</span> (use a real photo). New
                    images stage as preview — Accept to push live to the customer.
                </p>
            </div>

            {days.map((d, i) => (
                <DayPreviewCard
                    key={i}
                    index={i}
                    day={d}
                    orderId={orderId}
                    imageByKey={imageByKey}
                    onChange={onChange}
                    onImagesRefresh={onImagesRefresh}
                />
            ))}
        </div>
    );
}

function DayPreviewCard({ index, day, orderId, imageByKey, onChange, onImagesRefresh }) {
    const dayIdx = index + 1;
    const fmt = (day.format || "Single photo").toLowerCase();
    const isCarousel = fmt === "carousel";
    const isReel = fmt.startsWith("reel");
    const isStory = fmt.startsWith("story");
    const isImageDay = !isReel && !isStory;
    const slideCount = isCarousel ? 5 : 1;

    return (
        <div
            className="rounded-2xl border border-border bg-cafe-cream p-4 sm:p-5"
            data-testid={`day-preview-card-${dayIdx}`}
        >
            {/* Header — day badge + inline format/time editing */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold px-2.5 py-1 rounded-full bg-cafe-espresso text-cafe-cream">
                    Day {dayIdx}
                </span>
                <Input
                    value={day.format || ""}
                    onChange={(e) => onChange(`calendar_30d.${index}.format`, e.target.value)}
                    className="bg-transparent border-0 border-b border-border rounded-none px-0 h-7 text-xs text-cafe-mocha w-40 font-semibold uppercase tracking-[0.18em]"
                    placeholder="Format"
                />
                <Input
                    value={day.best_time || ""}
                    onChange={(e) => onChange(`calendar_30d.${index}.best_time`, e.target.value)}
                    className="bg-transparent border-0 border-b border-border rounded-none px-0 h-7 text-xs text-cafe-mocha w-32"
                    placeholder="Best time"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5 items-start">
                {/* IMAGE COLUMN — preview + edit controls. Story days: instructional banner. */}
                <div>
                    {isStory ? (
                        <div className="aspect-square rounded-xl border-2 border-dashed border-cafe-mocha/30 bg-cafe-latte/30 flex items-center justify-center p-4 text-center">
                            <p className="text-[11px] uppercase tracking-[0.2em] text-cafe-mocha font-semibold leading-relaxed">
                                Story-only · no AI image generated
                            </p>
                        </div>
                    ) : isReel ? (
                        <div className="aspect-square rounded-xl border-2 border-dashed border-cafe-terracotta/40 bg-cafe-terracotta/5 flex items-center justify-center p-4 text-center">
                            <p className="text-[11px] uppercase tracking-[0.2em] text-cafe-terracotta font-semibold leading-relaxed">
                                Reel · video managed in Video panel
                            </p>
                        </div>
                    ) : isCarousel ? (
                        <CarouselSlidesEditor
                            dayIdx={dayIdx}
                            slideCount={slideCount}
                            day={day}
                            orderId={orderId}
                            imageByKey={imageByKey}
                            onImagesRefresh={onImagesRefresh}
                        />
                    ) : (
                        <SingleImageEditor
                            dayIdx={dayIdx}
                            slide={1}
                            day={day}
                            orderId={orderId}
                            entry={imageByKey.get(`${dayIdx}-1`)}
                            onImagesRefresh={onImagesRefresh}
                        />
                    )}
                </div>

                {/* TEXT COLUMN — inline-editable theme/hook/caption/cta/shoot_note */}
                <div className="space-y-2.5">
                    <Input
                        value={day.theme || ""}
                        onChange={(e) => onChange(`calendar_30d.${index}.theme`, e.target.value)}
                        className="bg-transparent border-0 border-b border-border rounded-none px-0 h-9 text-cafe-espresso font-serif text-lg"
                        placeholder="Theme"
                        data-testid={`day-${dayIdx}-theme-input`}
                    />
                    <Input
                        value={day.hook || ""}
                        onChange={(e) => onChange(`calendar_30d.${index}.hook`, e.target.value)}
                        className="bg-cafe-latte/60 border-border rounded-xl text-sm italic font-display"
                        placeholder="Hook (1-line scroll-stopper)"
                        data-testid={`day-${dayIdx}-hook-input`}
                    />
                    <Textarea
                        rows={5}
                        value={day.caption || ""}
                        onChange={(e) => onChange(`calendar_30d.${index}.caption`, e.target.value)}
                        className="bg-cafe-latte/60 border-border rounded-xl text-sm leading-relaxed"
                        placeholder="Caption"
                        data-testid={`day-${dayIdx}-caption-input`}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input
                            value={day.cta || ""}
                            onChange={(e) => onChange(`calendar_30d.${index}.cta`, e.target.value)}
                            className="bg-cafe-olive/10 border-cafe-olive/30 rounded-xl text-xs"
                            placeholder="💬 CTA / engagement prompt"
                            data-testid={`day-${dayIdx}-cta-input`}
                        />
                        <Input
                            value={day.shoot_note || ""}
                            onChange={(e) => onChange(`calendar_30d.${index}.shoot_note`, e.target.value)}
                            className="bg-cafe-terracotta/5 border-cafe-terracotta/30 rounded-xl text-xs"
                            placeholder="📸 Shoot note"
                            data-testid={`day-${dayIdx}-shoot-input`}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Single image slot — supports re-roll / re-roll with hint / upload + accept/discard preview. */
function SingleImageEditor({ dayIdx, slide, day, orderId, entry, onImagesRefresh }) {
    const [busy, setBusy] = useState(null); // "rolling" | "uploading" | "accepting" | "discarding"
    const [showHint, setShowHint] = useState(false);
    const [hintText, setHintText] = useState("");
    const [venueRefUrl, setVenueRefUrl] = useState(null); // ad-hoc venue photo for this re-roll
    const fileRef = useRef(null);
    const venueFileRef = useRef(null);

    const liveUrl = entry?.url;
    const previewUrl = entry?.preview_url;
    const previewHint = entry?.preview_hint;
    const previewSource = entry?.preview_source;
    const hasPreview = !!previewUrl;

    const regen = async (hint) => {
        setBusy("rolling");
        try {
            const { data } = await axios.post(
                `${API}/orders/${orderId}/regen_image/${dayIdx}`,
                { slide, hint: hint || null, as_preview: true, venue_ref_url: venueRefUrl || null },
                { headers: studioHeaders() },
            );
            const venueNote = venueRefUrl ? " + venue ref" : "";
            toast.success(
                `Day ${dayIdx} re-rolled · staged as preview${venueNote}`,
                {
                    description: hint
                        ? `Hint: "${hint}"${venueRefUrl ? " · using ad-hoc venue photo" : ""}`
                        : (venueRefUrl ? "Using ad-hoc venue photo." : "Same prompt, fresh render."),
                    duration: 5000,
                },
            );
            setShowHint(false);
            setHintText("");
            setVenueRefUrl(null);
            onImagesRefresh?.();
            return data;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Re-roll failed");
        } finally {
            setBusy(null);
        }
    };

    /** Upload a one-off "venue" environment photo. Doesn't touch the order's
     *  ai_images — it's a transient anchor used only for the NEXT regen call.
     *  Cleared automatically once the regen completes (success or failure). */
    const uploadVenueRef = async (file) => {
        if (!file) return;
        setBusy("uploading-venue");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(
                `${API}/orders/${orderId}/venue_image?slot=3`,  // slot=3 reserved for studio ad-hoc
                fd,
                { headers: { ...studioHeaders(), "Content-Type": "multipart/form-data" } },
            );
            setVenueRefUrl(data.url);
            toast.success("Venue photo attached · click Generate to apply");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Venue upload failed");
        } finally {
            setBusy(null);
            if (venueFileRef.current) venueFileRef.current.value = "";
        }
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy("uploading");
        try {
            const form = new FormData();
            form.append("file", file);
            await axios.post(
                `${API}/orders/${orderId}/upload_image/${dayIdx}?slide=${slide}&as_preview=true`,
                form,
                { headers: { ...studioHeaders(), "Content-Type": "multipart/form-data" } },
            );
            toast.success(`Day ${dayIdx} upload staged as preview`, {
                description: "Accept to push live to the customer.",
                duration: 5000,
            });
            onImagesRefresh?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const acceptPreview = async () => {
        setBusy("accepting");
        try {
            await axios.post(
                `${API}/orders/${orderId}/accept_preview/${dayIdx}`,
                { slide },
                { headers: studioHeaders() },
            );
            toast.success(`Day ${dayIdx} preview accepted — customer sees the new image now`);
            onImagesRefresh?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Accept failed");
        } finally {
            setBusy(null);
        }
    };

    const discardPreview = async () => {
        setBusy("discarding");
        try {
            await axios.post(
                `${API}/orders/${orderId}/discard_preview/${dayIdx}`,
                { slide },
                { headers: studioHeaders() },
            );
            toast.success(`Day ${dayIdx} preview discarded`);
            onImagesRefresh?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Discard failed");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-2">
            {/* Image canvas — single image OR side-by-side compare when preview staged */}
            {hasPreview ? (
                <div className="grid grid-cols-2 gap-2">
                    <ImageThumb url={liveUrl} label="Current" testid={`day-${dayIdx}-current`} />
                    <ImageThumb
                        url={previewUrl}
                        label={`Preview${previewSource === "upload" ? " (upload)" : ""}`}
                        accent
                        testid={`day-${dayIdx}-preview`}
                    />
                </div>
            ) : (
                <ImageThumb url={liveUrl} label={liveUrl ? null : "No image yet"} testid={`day-${dayIdx}-current`} />
            )}

            {previewHint && (
                <p className="text-[11px] text-cafe-mocha/80 italic px-1">
                    Hint used: "{previewHint}"
                </p>
            )}

            {/* Accept / Discard row — only when there's a preview to act on */}
            {hasPreview && (
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        onClick={acceptPreview}
                        disabled={!!busy}
                        data-testid={`day-${dayIdx}-accept-preview`}
                        className="flex-1 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-8 text-xs font-semibold"
                    >
                        {busy === "accepting" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                        Accept
                    </Button>
                    <Button
                        size="sm"
                        onClick={discardPreview}
                        disabled={!!busy}
                        variant="outline"
                        data-testid={`day-${dayIdx}-discard-preview`}
                        className="flex-1 rounded-full border-border bg-transparent h-8 text-xs"
                    >
                        {busy === "discarding" ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
                        Discard
                    </Button>
                </div>
            )}

            {/* Action row — re-roll / hint / upload */}
            <div className="flex flex-wrap gap-1.5">
                <Button
                    size="sm"
                    onClick={() => regen()}
                    disabled={!!busy || hasPreview}
                    title={hasPreview ? "Accept or discard the current preview first" : "Re-roll with same prompt (~30s)"}
                    data-testid={`day-${dayIdx}-reroll`}
                    className="rounded-full bg-cafe-espresso hover:bg-black text-cafe-cream h-7 px-3 text-[11px] font-semibold disabled:opacity-50"
                >
                    {busy === "rolling" && !showHint ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                    Re-roll
                </Button>
                <Button
                    size="sm"
                    onClick={() => setShowHint((v) => !v)}
                    disabled={!!busy || hasPreview}
                    title="Add a 1-line directive (e.g. 'darker mood') and/or attach a venue photo to anchor the cafe environment"
                    data-testid={`day-${dayIdx}-toggle-hint`}
                    variant="outline"
                    className="rounded-full border-cafe-olive/50 bg-transparent text-cafe-olive h-7 px-3 text-[11px] font-semibold disabled:opacity-50"
                >
                    <MessageSquare className="h-3 w-3 mr-1" />
                    +Hint / Venue
                </Button>
                <Button
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={!!busy || hasPreview}
                    title="Upload a real product photo to replace the AI render"
                    data-testid={`day-${dayIdx}-upload`}
                    variant="outline"
                    className="rounded-full border-border bg-transparent h-7 px-3 text-[11px] font-semibold disabled:opacity-50"
                >
                    {busy === "uploading" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                    Upload
                </Button>
                <input
                    type="file"
                    ref={fileRef}
                    accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                    onChange={(e) => upload(e.target.files?.[0])}
                    className="hidden"
                    data-testid={`day-${dayIdx}-file-input`}
                />
            </div>

            {/* Hint composer — inline appears below buttons when toggled */}
            {showHint && (
                <div className="bg-cafe-olive/10 border border-cafe-olive/30 rounded-xl p-2.5 space-y-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-bold">
                        Steer the AI · 1 line
                    </p>
                    <Input
                        value={hintText}
                        onChange={(e) => setHintText(e.target.value)}
                        placeholder="e.g. darker mood, less orange, top-down view"
                        className="bg-cafe-cream border-border rounded-lg text-xs h-8"
                        data-testid={`day-${dayIdx}-hint-input`}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && hintText.trim() && !busy) {
                                regen(hintText.trim());
                            }
                        }}
                    />
                    <div className="flex flex-wrap gap-1">
                        {[
                            "Darker mood",
                            "Less orange, more neutral",
                            "Top-down flat-lay",
                            "Closer macro shot",
                            "Brighter, airier",
                            "More steam / texture",
                        ].map((suggestion) => (
                            <button
                                key={suggestion}
                                onClick={() => setHintText(suggestion)}
                                disabled={!!busy}
                                className="text-[10px] px-2 py-1 rounded-full bg-cafe-cream border border-border hover:bg-cafe-latte text-cafe-mocha"
                                data-testid={`day-${dayIdx}-hint-quick-${suggestion.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                    <Button
                        size="sm"
                        onClick={() => regen(hintText.trim() || null)}
                        disabled={(!hintText.trim() && !venueRefUrl) || !!busy}
                        data-testid={`day-${dayIdx}-hint-submit`}
                        className="w-full rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-7 text-[11px] font-semibold disabled:opacity-50"
                    >
                        {busy === "rolling" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                        Generate {hintText.trim() ? "with hint" : ""}{venueRefUrl ? (hintText.trim() ? " + venue" : "with venue ref") : ""}
                    </Button>

                    {/* Venue / environment reference — optional ad-hoc upload
                        that anchors the cafe environment (walls/lighting/decor)
                        for THIS single re-roll only. Used when the AI's
                        invented venue doesn't match the merchant's actual shop. */}
                    <div className="pt-2 border-t border-cafe-olive/20">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-cafe-olive font-bold">
                                Venue reference <span className="text-cafe-mocha/70 normal-case font-normal tracking-normal">(env anchor)</span>
                            </p>
                            {venueRefUrl && (
                                <button
                                    onClick={() => setVenueRefUrl(null)}
                                    className="text-[10px] text-cafe-terracotta underline"
                                    data-testid={`day-${dayIdx}-venue-clear`}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        {venueRefUrl ? (
                            <div className="flex items-center gap-2">
                                <img src={venueRefUrl} alt="Venue ref" className="w-12 h-12 rounded border border-border object-cover" />
                                <p className="text-[10px] text-cafe-mocha leading-snug">
                                    Will use this photo as the cafe's environment anchor for this re-roll.
                                </p>
                            </div>
                        ) : (
                            <>
                                <Button
                                    size="sm"
                                    onClick={() => venueFileRef.current?.click()}
                                    disabled={!!busy}
                                    variant="outline"
                                    data-testid={`day-${dayIdx}-venue-upload`}
                                    className="w-full rounded-full border-cafe-olive/40 bg-cafe-cream h-7 text-[10px] font-semibold disabled:opacity-50"
                                >
                                    {busy === "uploading-venue" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                                    Upload venue photo
                                </Button>
                                <p className="text-[10px] text-cafe-mocha/70 italic mt-1">
                                    Optional. Use when AI gets the cafe environment wrong.
                                </p>
                            </>
                        )}
                        <input
                            type="file"
                            ref={venueFileRef}
                            accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                            onChange={(e) => uploadVenueRef(e.target.files?.[0])}
                            className="hidden"
                            data-testid={`day-${dayIdx}-venue-file-input`}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

/** Carousel slot — renders 5 mini editors stacked vertically, one per slide. */
function CarouselSlidesEditor({ dayIdx, slideCount, day, orderId, imageByKey, onImagesRefresh }) {
    return (
        <div className="space-y-3">
            {Array.from({ length: slideCount }, (_, i) => i + 1).map((slide) => (
                <div key={slide} className="border border-border/60 rounded-xl p-2 bg-cafe-cream">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-cafe-terracotta font-bold mb-1.5">
                        Slide {slide}/{slideCount}
                    </p>
                    <SingleImageEditor
                        dayIdx={dayIdx}
                        slide={slide}
                        day={day}
                        orderId={orderId}
                        entry={imageByKey.get(`${dayIdx}-${slide}`)}
                        onImagesRefresh={onImagesRefresh}
                    />
                </div>
            ))}
        </div>
    );
}

function ImageThumb({ url, label, accent = false, testid }) {
    return (
        <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-cafe-latte border border-border" data-testid={testid}>
            {url ? (
                <img src={absUrl(url)} alt={label || "preview"} className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-[11px] text-cafe-mocha/60 italic">
                    {label || "—"}
                </div>
            )}
            {label && (
                <span
                    className={`absolute top-1.5 left-1.5 text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded ${
                        accent
                            ? "bg-cafe-terracotta text-cafe-cream"
                            : "bg-cafe-espresso/85 text-cafe-cream"
                    }`}
                >
                    {label}
                </span>
            )}
        </div>
    );
}
